import { getToken } from "@/scripts/Common";
import type { ApiResponse } from "@/scripts/Common/api";
import { defaultFetch, httpMethod } from "@/scripts/Common/api";
import type { CreateApiKeyModel, DeviceApiKeyDto, FetchCustomerApiKeysParam } from "./types";

const fetchCustomerApiKeys = async (
  customerId: number,
  params: FetchCustomerApiKeysParam,
  signal?: AbortSignal
): Promise<ApiResponse<DeviceApiKeyDto[]>> => {
  const {
    response,
    error,
    paginationCount,
    paginationLimit,
    paginationPage,
    totalItemCount
  } = await defaultFetch({
    apiName: 'customerApiKeys',
    bearer: getToken(),
    options: {
      method: httpMethod.GET,
      routeParams: {
        customerId: customerId.toString()
      },
      query: {
        pageNumber: params.pageNumber ? Math.max(params.pageNumber - 1, 0) : 0, // Convert 1-based to 0-based
        pageSize: params.pageSize,
        hasDevice: params.hasDevice ?? false
      },
      abortSignal: signal
    }
  });

  return {
    response,
    error,
    paginationCount,
    paginationLimit,
    paginationPage,
    totalItemCount
  };
};

const createCustomerApiKey = async (
  customerId: number,
  model: CreateApiKeyModel
): Promise<ApiResponse<number>> => await defaultFetch({
  apiName: 'customerApiKeysExternal',
  bearer: getToken(),
  options: {
    method: httpMethod.POST,
    routeParams: {
      customerId: customerId.toString()
    }
  },
  body: JSON.stringify({
    description: model.description
  })
});

const deleteCustomerApiKey = async (
  customerId: number,
  apiKeyId: number
): Promise<{ error: any }> => {
  const { error } = await defaultFetch({
    apiName: 'customerApiKey',
    bearer: getToken(),
    options: {
      method: httpMethod.DELETE,
      routeParams: {
        customerId: customerId.toString(),
        apiKeyId: apiKeyId.toString()
      }
    }
  });

  return {
    error
  };
};

const rotateCustomerApiKey = async (
  customerId: number,
  apiKeyId: number
): Promise<{ error: any }> => {
  const { error } = await defaultFetch({
    apiName: 'customerApiKeyRotate',
    bearer: getToken(),
    options: {
      method: httpMethod.PUT,
      routeParams: {
        customerId: customerId.toString(),
        apiKeyId: apiKeyId.toString()
      }
    }
  });

  return {
    error
  };
};

export {
  fetchCustomerApiKeys,
  createCustomerApiKey,
  deleteCustomerApiKey,
  rotateCustomerApiKey
};
